import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { forwardRef, ReactElement, ReactNode } from 'react';

import { cn } from './cn';

export const TooltipProvider = TooltipPrimitive.Provider;
/**
 * Carries its own provider so a tooltip works anywhere, including in tests and
 * portals rendered outside the app shell.
 */
export const Tooltip = ({ delayDuration = 100, ...props }: TooltipPrimitive.TooltipProps) => (
  <TooltipPrimitive.Provider delayDuration={delayDuration}>
    <TooltipPrimitive.Root {...props} />
  </TooltipPrimitive.Provider>
);
export const TooltipTrigger = TooltipPrimitive.Trigger;

export const TooltipContent = forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ children, className, sideOffset = 4, collisionPadding = 8, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      className={cn(
        'z-tooltip max-w-xs rounded-auditor border border-ink bg-ink px-2.5 py-1.5',
        'text-xs font-medium leading-snug text-ink-inverse shadow-auditor-overlay',
        'text-pretty wrap-break-word',
        'opacity-0 transition-opacity data-[state=delayed-open]:opacity-100 data-[state=instant-open]:opacity-100',
        className,
      )}
      {...props}
    >
      {children}
      <TooltipPrimitive.Arrow className="fill-ink" width={11} height={6} />
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * A tooltip for the common case: one element, one hint. Renders the child
 * untouched when there is nothing to say, so callers can pass a conditional title.
 */
export const SimpleTooltip = ({
  children,
  className,
  open,
  side,
  title,
}: {
  children: ReactElement;
  className?: string;
  /** Forces the tooltip open or shut, for transient confirmations such as "Copied!". */
  open?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
  title: ReactNode;
}) => {
  if (title === null || title === undefined || title === false || title === '') {
    return children;
  }
  return (
    <Tooltip open={open}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} className={className}>
        {title}
      </TooltipContent>
    </Tooltip>
  );
};
